import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { E_DisplayType } from '../enums/display-type.enum';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { BackendLocaleMap, E_SupportedLocales } from '../enums/locales.enum';
import { ConfigService } from '../utils/config.service';
import { I_NoticeResponse } from '../models/display/items/notices/api/notices.response';
import { I_CurrentDisplay, I_DisplayResponse, I_DisplaySelectResponse, I_DisplaysResponse } from '../models/display/api/display.response';
import { C_DisplayDataContainer, C_DisplayData } from '../models/display/domain/display.model';
import { SmartRotativeLayoutsService } from '../../modules/main/pages/standard/pages/home/customization/smart-home/smart-rotative-layoutst.service';
@Injectable({
  providedIn: 'root'
})
export class DisplaysService {
  private router = inject(Router);
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private locale = inject(LOCALE_ID);
  private cfg = inject(ConfigService);
  private rotativeDisplaysService = inject(SmartRotativeLayoutsService);

  // STATE MANAGEMENT
  private initializedSubject = new BehaviorSubject<boolean>(false);
  private initializingSubject = new BehaviorSubject<boolean>(false);
  private isLoadingDisplay = new BehaviorSubject<boolean>(false);
  private currentDisplay = new BehaviorSubject<I_CurrentDisplay | null>(null);
  private displaysContainer = new C_DisplayDataContainer();
  private currentUrl = this.cfg.get("apiUrl");

  // SEMÁFORO: Caché de peticiones HTTP en curso
  private pendingRequests = new Map<string, Observable<C_DisplayData | undefined>>();

  constructor() {
    this.currentDisplay.subscribe(disp => this.authService.setCurrentDisplayId(disp?.id));
    this.authService.getResetDisplayStatus().subscribe(reset => {
      if (reset) this.reset();
    });
  }

  getCurrentDisplay(): Observable<I_CurrentDisplay | null> {
    return this.currentDisplay.asObservable();
  }

  removeDisplay() {
    this.currentDisplay.next(null);
  }

  reset() {
    this.currentDisplay.next(null);
    this.displaysContainer.reset();
    this.initializedSubject.next(false);
    this.initializingSubject.next(false);
    this.isLoadingDisplay.next(false);
    this.rotativeDisplaysService.stopRotating();
    this.authService.setResetDisplayStatus(false);
    this.pendingRequests.clear();
  }

  isLoadingADisplay(): Observable<boolean> {
    return this.isLoadingDisplay.asObservable();
  }

  /**
   * Actualización Síncrona Interna: Actualiza el estado sin hacer peticiones extra.
   */
  private updateCurrentDisplaySync(dispId: string, dispType: E_DisplayType): void {
    const currentDisp = this.currentDisplay.getValue();
    if (currentDisp?.id === dispId && currentDisp?.type === dispType) return;

    this.rotativeDisplaysService.stopRotating();
    this.currentDisplay.next({ id: dispId, type: dispType });
  }

  /**
   * Actualiza el display actual. Si falta el tipo, lo busca primero de forma reactiva.
   */
  updateCurrentDisplay(dispId: string, dispType?: E_DisplayType): Observable<void> {
    if (dispType) {
      this.updateCurrentDisplaySync(dispId, dispType);
      return of(undefined);
    }

    // Si no tenemos el tipo, delegamos a getDisplay que se encarga de la caché y peticiones
    return this.getDisplay(dispId).pipe(
      tap(disp => {
        if (disp?.displayInfo?.type) {
          this.updateCurrentDisplaySync(dispId, disp.displayInfo.type);
        }
      }),
      map(() => undefined)
    );
  }

  /**
   * MÉTODO CORE UNIFICADO: Busca en memoria -> Busca en Peticiones en curso -> Hace HTTP.
   */
  getDisplay(id: string, type?: E_DisplayType): Observable<C_DisplayData | undefined> {
    // 1. Búsqueda en Memoria (Contenedor local)
    let cachedDisplay: C_DisplayData | undefined = undefined;

    if (type && this.displaysContainer.has(type)) {
      cachedDisplay = this.displaysContainer.get(type)?.get(id);
    } else {
      const values = Object.values(E_DisplayType);
      for (const t of values) {
        const list = this.displaysContainer.get(t);
        if (list && list.has(id)) {
          cachedDisplay = list.get(id);
          type = cachedDisplay?.displayInfo?.type;
          break;
        }
      }
    }

    if (cachedDisplay) {
      if (type) this.updateCurrentDisplaySync(id, type);
      return of(cachedDisplay);
    }

    // 2. Búsqueda en Peticiones en Curso (Semáforo)
    // Si alguien más ya pidió este ID hace milisegundos, nos unimos a su observable
    if (this.pendingRequests.has(id)) {
      return this.pendingRequests.get(id)!.pipe(
        tap(disp => {
          if (disp?.displayInfo?.type) this.updateCurrentDisplaySync(id, disp.displayInfo.type);
        })
      );
    }

    // 3. Crear Nueva Petición HTTP Reactiva
    this.isLoadingDisplay.next(true);
    const locale = BackendLocaleMap[this.locale as E_SupportedLocales];
    const url = `${this.currentUrl}/displays/${locale}/${id}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Source': 'Display',
        'DisplayId': id
      })
    };

    const request$ = this.http.get<I_DisplayResponse>(url, httpOptions).pipe(
      map(resp => {
        this.initializedSubject.next(true);
        this.authService.initializeRoles(resp.display.id, resp.permissions);
        this.initializingSubject.next(false);

        // Resolución de tipo
        let respType = type || (resp.display.type === E_DisplayType.DisplayNurse
          ? E_DisplayType.DisplayNurse
          : E_DisplayType.SmartDisplay);

        // Guardado en contenedor
        if (!this.displaysContainer.has(respType)) {
          this.displaysContainer.set(respType);
          this.displaysContainer.forceUpdateDetection();
        }

        const list = this.displaysContainer.get(respType)!;
        list.setDisplay(resp.display);
        list.forceUpdateDetection();

        return list.get(id); // Retornamos el modelo de dominio procesado
      }),
      tap(disp => {
        if (disp?.displayInfo?.type) {
          this.updateCurrentDisplaySync(id, disp.displayInfo.type);
        }
      }),
      catchError(err => {
        console.error("Error fetching display data:", err);
        this.initializedSubject.next(true);
        this.initializingSubject.next(false);
        this.router.navigate(["/"], { queryParamsHandling: 'preserve' });
        return of(undefined);
      }),
      finalize(() => {
        this.isLoadingDisplay.next(false);
        this.pendingRequests.delete(id); // Limpiamos la caché de vuelo termine bien o mal
      }),
      shareReplay(1) // Comparte esta petición con cualquiera que se suscriba mientras está activa
    );

    // Guardamos la petición en curso y la retornamos
    this.pendingRequests.set(id, request$);
    return request$;
  }

  /**
   * Transformado a puro Observable
   */
  getDisplays(): Observable<I_DisplaysResponse> {
    return this.http.get<I_DisplayResponse[]>(`${this.currentUrl}/displays`).pipe(
      map(datos => {
        this.initializedSubject.next(true);
        this.initializingSubject.next(false);
        return { error: false, data: datos };
      }),
      catchError(() => {
        this.initializedSubject.next(true);
        this.initializingSubject.next(false);
        return of({ error: true, data: [] });
      })
    );
  }


  updateDisplaysAuthoritiesSync(allDisplays: I_DisplayResponse[]): void {
    const incomingTypes = [...new Set(allDisplays.map(m => m.display.type))];
    incomingTypes.forEach(type => {
      const containerList = this.displaysContainer.get(type);
      if (!containerList) return;
      const cachedInType = containerList.getValues();
      cachedInType.forEach(display => {
        const currentSectionList = display.displayInfo.displaySectionList || [];
        display.displayInfo.displaySectionList = allDisplays.reduce((acc, item) => {
          if (item.display.type === type) {
            const existing = currentSectionList.find(c => c.id === item.display.id);
            acc.push({
              id: item.display.id,
              name: item.display.name,
              isSelected: !!existing?.isSelected
            });
          }
          return acc;
        }, [] as I_DisplaySelectResponse[]);
        display.displayInfo.forceUpdateDetection();
      });
    });
  }


  updateNotice(notice: I_NoticeResponse) { return this.http.put(`${this.currentUrl}/notices/${notice.id}`, notice); }
  insertNotice(notice: I_NoticeResponse) { return this.http.post(`${this.currentUrl}/notices`, notice); }
  deleteNotice(noticeId: string) { return this.http.delete(`${this.currentUrl}/notices/${noticeId}`); }
  getPocsAvailables(displayIdList: string[]): Observable<I_AvailablePoCs> {
    return this.http.post<I_AvailablePoCs>(`${this.currentUrl}/displays/poc-available`, displayIdList);
  }
  getPocsWithStatus(displayIdList: string[]): Observable<I_AvailablePoCs> {
    return this.http.post<I_AvailablePoCs>(`${this.currentUrl}/displays/poc-with-status`, displayIdList);
  }
}

interface I_AvailablePoCs { pocList: I_MinimalPocAndUnitDto[]; }
interface I_MinimalPocAndUnitDto { pocName: string; pocId: string; unitName: string; unitId: string; }
